import { useEffect } from 'react'
import { isPathInWhitelist, parseRegionCookie } from './utils.js'
import type { RegionConfig } from './type.js'

export interface UseSavedRegionRedirectOptions {
  /**
   * 从 cookie 中读取的已保存地区信息（格式：'DE:de'）
   */
  savedRegion: string | null

  /**
   * 当前站点的地区代码
   */
  currentRegionCode: string

  /**
   * 地区配置列表
   */
  regions: RegionConfig[]

  /**
   * 白名单路径（在白名单中不执行跳转）
   */
  excludePaths?: string[]

  /**
   * 当前路径
   */
  pathname?: string

  /**
   * 用户是否手动关闭了横幅（关闭横幅后不再自动跳转）
   */
  userDismissedBanner?: boolean

  /**
   * 自动跳转处理函数
   */
  onRedirect: (region: RegionConfig, isCookieRedirect?: boolean) => void | Promise<void>
}

/**
 * Cookie 保存地区跳转监听 Hook
 *
 * 监听用户之前保存的地区偏好（存储在 cookie 中），
 * 如果保存的地区与当前地区不匹配，自动跳转到保存的地区
 *
 * @example
 * ```tsx
 * useSavedRegionRedirect({
 *   savedRegion,           // 从 useIPRedirect 获取
 *   currentRegionCode,
 *   regions,
 *   excludePaths,
 *   pathname,
 *   userDismissedBanner,
 *   onRedirect: handleAutoRedirect,  // 从 useAutoRedirect 获取
 * })
 * ```
 */
export function useSavedRegionRedirect(options: UseSavedRegionRedirectOptions): void {
  const {
    savedRegion,
    currentRegionCode,
    regions,
    excludePaths = [],
    pathname = '',
    userDismissedBanner = false,
    onRedirect,
  } = options

  useEffect(() => {
    // 白名单检查：如果当前 URL/路径在白名单中，不执行自动跳转
    if (isPathInWhitelist(pathname, excludePaths)) {
      return
    }

    // 如果用户已经手动关闭横幅，不再自动跳转
    if (userDismissedBanner) {
      return
    }

    // 如果没有保存的地区偏好，不处理
    if (!savedRegion) {
      return
    }

    // 解析 cookie 中保存的地区代码
    const parsed = parseRegionCookie(savedRegion)
    if (!parsed) {
      return
    }

    const { regionCode: savedRegionCode } = parsed

    // 如果保存的地区与当前地区匹配，不处理
    if (savedRegionCode === currentRegionCode) {
      return
    }

    // 查找保存的地区配置
    const targetRegion = regions.find(r => r.code === savedRegionCode)
    if (!targetRegion) {
      return
    }

    // 使用 onRedirect 跳转（复用页面检查和 UTM 处理逻辑）
    onRedirect(targetRegion, true)
  }, [savedRegion, currentRegionCode, regions, userDismissedBanner, onRedirect, pathname, excludePaths])
}
